import api from "./api";

export interface SiteBranding {
  siteName: string;
  siteTitle: string;
  siteFavicon: string;
  navbarLogo?: string;
  footerLogo?: string;
  favicon?: string;
}

// Cache for site branding data
let brandingCache: SiteBranding | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getSiteBranding = async (): Promise<SiteBranding> => {
  const now = Date.now();

  // Return cached data if still valid
  if (brandingCache && now - lastFetchTime < CACHE_DURATION) {
    return brandingCache;
  }

  try {
    const response = await api.get("/landing-page/content");
    const data = response.data || {};
    const settings = data.settings || {};
    const logos = data.logos || [];

    // Extract branding data from settings
    const siteName = settings.site_name || "ເພັດທະບຸນ ສະຫວັດ";
    const siteTitle = settings.site_title || "ເພັດທະບຸນ ສະຫວັດ ສູນການຮຽນຮູ້";
    const siteFavicon = settings.site_favicon || "/favicon.png";

    // Find navbar and footer logos
    const navbarLogo = logos?.find(
      (logo: any) => logo.logo_type === "navbar" && logo.is_active,
    )?.logo_url;
    const footerLogo = logos?.find(
      (logo: any) => logo.logo_type === "footer" && logo.is_active,
    )?.logo_url;
    const favicon =
      logos?.find((logo: any) => logo.logo_type === "favicon" && logo.is_active)
        ?.logo_url || siteFavicon;

    const branding: SiteBranding = {
      siteName,
      siteTitle,
      siteFavicon,
      navbarLogo,
      footerLogo,
      favicon,
    };

    // Update cache
    brandingCache = branding;
    lastFetchTime = now;

    return branding;
  } catch (error) {
    console.error("Error fetching site branding:", error);

    // Return default branding on error
    return {
      siteName: "ເພັດທະບຸນ ສະຫວັດ",
      siteTitle: "ເພັດທະບຸນ ສະຫວັດ ສູນການຮຽນຮູ້",
      siteFavicon: "/favicon.png",
    };
  }
};

export const updateDocumentHead = (branding: SiteBranding) => {
  // Update document title
  if (branding.siteTitle) {
    document.title = branding.siteTitle;
  }

  // Update favicon
  if (branding.favicon) {
    const faviconLink = document.querySelector(
      'link[rel="icon"]',
    ) as HTMLLinkElement;
    if (faviconLink) {
      faviconLink.href = branding.favicon;
    } else {
      // Create favicon link if it doesn't exist
      const newFaviconLink = document.createElement("link");
      newFaviconLink.rel = "icon";
      newFaviconLink.href = branding.favicon;
      document.head.appendChild(newFaviconLink);
    }
  }

  // Update apple touch icon
  if (branding.favicon) {
    const appleTouchIcon = document.querySelector(
      'link[rel="apple-touch-icon"]',
    ) as HTMLLinkElement;
    if (appleTouchIcon) {
      appleTouchIcon.href = branding.favicon;
    } else {
      // Create apple touch icon link if it doesn't exist
      const newAppleTouchIcon = document.createElement("link");
      newAppleTouchIcon.rel = "apple-touch-icon";
      newAppleTouchIcon.href = branding.favicon;
      document.head.appendChild(newAppleTouchIcon);
    }
  }
};

export const clearBrandingCache = () => {
  brandingCache = null;
  lastFetchTime = 0;
};

export const refreshBranding = async () => {
  clearBrandingCache();
  const branding = await getSiteBranding();
  updateDocumentHead(branding);
  return branding;
};
